'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { Logo } from './Logo';
import { Spinner } from './Spinner';
import { trackEvent } from '@/lib/analytics';
import { createApiClient, type MeResponse } from '@/lib/api';
import { getInitData, hapticImpact, hapticNotification, isInTelegram } from '@/lib/tma';

const FALLBACK_KEY = 'prognoz_agreement_v1';

interface Screen {
  color: 'lime' | 'pink';
  label: string;
  title: string[];
  accent: number;
  body: string;
  steps?: ReadonlyArray<readonly [string, string, string]>;
}

const SCREENS: Screen[] = [
  {
    color: 'lime',
    label: '01 / 03',
    title: ['СТАВЬ', 'НА ТО, ЧТО', 'СБУДЕТСЯ'],
    accent: 1,
    body:
      'Pari-mutuel биржа политических прогнозов. Это не букмекер и не казино — игроки ставят друг против друга, платформа берёт 7% с пула проигравших.',
  },
  {
    color: 'pink',
    label: '02 / 03',
    title: ['ТВОИ', 'РИСКИ'],
    accent: -1,
    body:
      'Это реальные деньги. При неверном предсказании ты теряешь всю сумму. Размещай только то, что готов потерять.',
    steps: [
      ['!', 'РЕАЛЬНЫЕ ПОТЕРИ', 'нет страховки и компенсаций'],
      ['18+', 'ТОЛЬКО ВЗРОСЛЫМ', 'и не из заблокированных стран'],
      ['7%', 'КОМИССИЯ', 'удерживается с пула проигравших'],
    ],
  },
  {
    color: 'lime',
    label: '03 / 03',
    title: ['ПРИНЯТЬ', 'УСЛОВИЯ'],
    accent: 1,
    body:
      'Чтобы начать пользоваться платформой, подтверди ключевые условия. Без согласия размещение предсказаний и пополнение баланса недоступны.',
  },
];

interface OnboardingProps {
  /**
   * undefined — данные ещё не загружены, ждём
   * null — нет initData (вне Telegram, dev/preview): fallback на localStorage
   * object — статус с сервера
   */
  agreement?: MeResponse['agreement'] | null;
  onAccepted?: () => void;
}

export function Onboarding({ agreement, onAccepted }: OnboardingProps) {
  const [step, setStep] = useState(0);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [confirmAge, setConfirmAge] = useState(false);
  const [confirmJur, setConfirmJur] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localFallbackAccepted, setLocalFallbackAccepted] = useState<boolean | null>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);

  // localStorage fallback читаем только когда server agreement недоступен
  // (вне Telegram / preview-режим). В Telegram источник истины — сервер.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (agreement !== null) return;
    setLocalFallbackAccepted(localStorage.getItem(FALLBACK_KEY) === '1');
  }, [agreement]);

  // На больших экранах summary может полностью влезть без скролла —
  // в этом случае scroll-event никогда не сработает, поэтому проверяем
  // явно по размерам после рендера. ВАЖНО: этот хук должен быть ДО early
  // return'ов, иначе React error #310 (Rendered fewer hooks than expected).
  useEffect(() => {
    if (step !== 2 || scrolledToEnd) return;
    const el = summaryRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 12) {
      setScrolledToEnd(true);
    }
  }, [step, scrolledToEnd]);

  // ===== Все hooks выше — early returns ниже =====

  // Решение, показывать ли онбординг:
  if (agreement === undefined) return null; // ждём
  const accepted =
    agreement === null ? localFallbackAccepted === true : agreement.accepted;
  if (accepted) return null;
  // Если fallback ещё не прочитан и agreement === null — ждём один тик.
  if (agreement === null && localFallbackAccepted === null) return null;

  const screen = SCREENS[step]!;
  const isLast = step === SCREENS.length - 1;
  // Цвет акцента — lime (accent) или pink (no). Все ниже Tailwind-classы
  // строятся условно, без inline-хексов. Утилиты `glow-lime`/`glow-pink` для
  // radial-gradient объявлены в globals.css.
  const isLime = screen.color === 'lime';
  const accentText = isLime ? 'text-accent' : 'text-no';
  const accentBg = isLime ? 'bg-accent' : 'bg-no';
  const glowClass = isLime ? 'glow-lime' : 'glow-pink';
  const allConfirmed = confirmAge && confirmJur && confirmTerms;
  const canAccept = scrolledToEnd && allConfirmed && !submitting;

  function handleNext() {
    hapticImpact('light');
    if (step < SCREENS.length - 1) setStep(step + 1);
  }

  function handleBack() {
    hapticImpact('light');
    if (step > 0) setStep(step - 1);
  }

  async function handleAccept() {
    if (!canAccept) return;
    setError(null);
    setSubmitting(true);
    try {
      if (isInTelegram() && getInitData()) {
        const api = createApiClient(getInitData);
        await api.acceptAgreement({
          confirmed_age_18: true,
          confirmed_jurisdiction: true,
          confirmed_terms_accepted: true,
        });
      }
      if (typeof window !== 'undefined') {
        localStorage.setItem(FALLBACK_KEY, '1');
      }
      trackEvent('terms_accepted');
      trackEvent('onboarding_completed');
      hapticNotification('success');
      setLocalFallbackAccepted(true);
      onAccepted?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Не удалось сохранить';
      setError(msg);
      hapticNotification('error');
      setSubmitting(false);
    }
  }

  function onSummaryScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    // 12px tolerance чтобы не зависеть от sub-pixel rounding.
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) {
      setScrolledToEnd(true);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-bg-base flex flex-col"
      style={{ overscrollBehavior: 'none' }}
    >
      {/* radial glow */}
      <div
        className={`pointer-events-none absolute ${glowClass}`}
        style={{
          top: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 500,
          height: 500,
        }}
      />

      <div className="px-7 pt-12 flex items-center justify-between">
        <Logo size={32} />
        {!isLast && (
          <span className="font-mono text-[10px] tracking-[0.15em] text-text-tertiary">
            ОБЯЗАТЕЛЬНОЕ СОГЛАСИЕ
          </span>
        )}
      </div>

      <div className="flex-1 px-7 pt-12 relative overflow-y-auto flex flex-col min-h-0">
        <div className={`font-mono text-[11px] tracking-[0.2em] mb-3.5 ${accentText}`}>
          ● {screen.label}
        </div>
        <h1 className="display text-[42px] leading-[0.9] tracking-[-0.03em] mb-5">
          {screen.title.map((line, i) => (
            <span key={i}>
              {i === screen.accent ? (
                <span className={accentText}>{line}</span>
              ) : (
                line
              )}
              <br />
            </span>
          ))}
        </h1>
        <p className="text-[14px] text-text-tertiary leading-[1.5] mb-5">{screen.body}</p>

        {step === 1 && screen.steps && (
          <div className="space-y-3">
            {screen.steps.map((s) => {
              const [n, t, d] = s;
              return (
                <div key={n} className="flex gap-3.5 items-start">
                  <div className={`display text-[26px] leading-[0.85] w-9 shrink-0 ${accentText}`}>
                    {n}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="display text-[14px] tracking-[-0.01em] mb-0.5">{t}</div>
                    <div className="text-[12px] text-text-tertiary">{d}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {step === 0 && (
          <div className="bg-bg-elev p-3.5 relative overflow-hidden border-l-[3px] border-accent">
            <div className="flex items-center gap-1.5 mb-2 font-mono text-[9px] tracking-[0.15em] font-bold">
              <span className="bg-accent text-bg-base px-1.5 py-0.5">ПРИМЕР</span>
              <span className="text-accent">● LIVE</span>
            </div>
            <div className="display text-[14px] mb-2.5">САНДУ В 1-М ТУРЕ?</div>
            <div className="flex items-baseline gap-2.5">
              <span className="display text-[34px] text-accent leading-[0.85]">62%</span>
              <span className="font-mono text-[11px] text-accent font-bold">↑ +4.2</span>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-3 pb-4">
            <div
              ref={summaryRef}
              onScroll={onSummaryScroll}
              className="max-h-[240px] overflow-y-auto bg-bg-elev p-3 text-[12px] leading-[1.55] scrollbar-hide border-l-2 border-line"
            >
              <div className="font-mono text-[10px] tracking-[0.15em] text-text-tertiary mb-2">
                КЛЮЧЕВЫЕ ТЕЗИСЫ
              </div>
              <ul className="space-y-2 text-text-secondary">
                <li>
                  <b className="text-white">Pari-mutuel.</b> Платформа — не букмекер и не
                  казино. Игроки ставят друг против друга, мы берём 7% с пула
                  проигравших.
                </li>
                <li>
                  <b className="text-white">18+.</b> Использование разрешено только
                  совершеннолетним.
                </li>
                <li>
                  <b className="text-white">Заблокированные юрисдикции.</b> Сервис
                  недоступен резидентам США, Великобритании, Франции, Германии,
                  Нидерландов, Австралии, Таиланда, Сингапура, Италии, Польши,
                  Швейцарии, Онтарио, Тайваня. VPN для обхода запрещён.
                </li>
                <li>
                  <b className="text-white">Реальные деньги.</b> При неверном
                  предсказании ты теряешь всю размещённую сумму. Это не инвестиция и не
                  совет.
                </li>
                <li>
                  <b className="text-white">Резолюция.</b> Решение по итогу события
                  принимаем мы на основании официальных источников, указанных в карточке
                  рынка. У тебя 24 часа на диспут после публикации резолюции.
                </li>
                <li>
                  <b className="text-white">Налоги.</b> Декларирование выигрышей и уплата
                  налогов — твоя ответственность.
                </li>
                <li>
                  <b className="text-white">Один аккаунт.</b> Множественные аккаунты,
                  боты и манипулирование — основание для блокировки и конфискации
                  средств.
                </li>
                <li>
                  Полные{' '}
                  <Link
                    href="/terms"
                    className="underline text-accent"
                    target="_blank"
                  >
                    Условия использования
                  </Link>{' '}
                  и{' '}
                  <Link
                    href="/privacy"
                    className="underline text-accent"
                    target="_blank"
                  >
                    Политика конфиденциальности
                  </Link>
                  .
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-line text-text-tertiary text-[10px] font-mono tracking-[0.05em]">
                {scrolledToEnd
                  ? '✓ ПРОЧИТАНО'
                  : '↓ ПРОКРУТИ ДО КОНЦА, ЧТОБЫ ПРОДОЛЖИТЬ'}
              </div>
            </div>

            <div className="space-y-2.5">
              <CheckRow
                checked={confirmAge}
                onChange={setConfirmAge}
                disabled={!scrolledToEnd}
                label="Мне исполнилось 18 лет."
              />
              <CheckRow
                checked={confirmJur}
                onChange={setConfirmJur}
                disabled={!scrolledToEnd}
                label="Я не нахожусь в заблокированной юрисдикции и не являюсь её резидентом."
              />
              <CheckRow
                checked={confirmTerms}
                onChange={setConfirmTerms}
                disabled={!scrolledToEnd}
                label={
                  <>
                    Я прочитал и принимаю{' '}
                    <Link
                      href="/terms"
                      className="underline text-accent"
                      target="_blank"
                    >
                      Условия использования
                    </Link>{' '}
                    и{' '}
                    <Link
                      href="/privacy"
                      className="underline text-accent"
                      target="_blank"
                    >
                      Политику конфиденциальности
                    </Link>
                    .
                  </>
                }
              />
            </div>

            {error && (
              <div className="bg-bg-elev border-l-2 border-no p-2.5 mb-3 font-mono text-[11px] text-no">
                ⚠️ {error}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-7 pb-8 pt-4">
        <div className="flex gap-1.5 mb-4">
          {SCREENS.map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-[3px] transition-colors ${
                i === step ? accentBg : 'bg-text-dim'
              }`}
            />
          ))}
        </div>
        {!isLast ? (
          <button
            onClick={handleNext}
            className={`sliced w-full py-4 display text-[14px] tracking-[0.12em] active:scale-[0.97] transition-transform ${accentBg} text-bg-base`}
          >
            ДАЛЬШЕ →
          </button>
        ) : (
          <button
            onClick={handleAccept}
            disabled={!canAccept}
            className={`sliced w-full py-4 display text-[14px] tracking-[0.12em] active:scale-[0.97] transition-transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${accentBg} text-bg-base`}
          >
            {submitting ? (
              <>
                <Spinner /> ОТПРАВКА…
              </>
            ) : !scrolledToEnd ? (
              'ПРОКРУТИ ДО КОНЦА'
            ) : !allConfirmed ? (
              'ОТМЕТЬ ВСЕ ПУНКТЫ'
            ) : (
              'ПРИНЯТЬ И НАЧАТЬ →'
            )}
          </button>
        )}
        {step > 0 && !submitting && (
          <button
            onClick={handleBack}
            className="block w-full mt-3 font-mono text-[11px] text-text-tertiary tracking-[0.15em]"
          >
            ← НАЗАД
          </button>
        )}
      </div>
    </div>
  );
}

interface CheckRowProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: React.ReactNode;
}

function CheckRow({ checked, onChange, disabled, label }: CheckRowProps) {
  // Step 2 (соглашение) всегда lime, поэтому accent — фиксированный.
  const borderClass = checked ? 'border-accent' : 'border-text-dim';
  const boxClass = checked
    ? 'bg-accent text-bg-base border-accent'
    : 'bg-transparent text-transparent border-text-dim';
  return (
    <button
      type="button"
      onClick={() => {
        if (disabled) return;
        hapticImpact('light');
        onChange(!checked);
      }}
      disabled={disabled}
      className={`w-full text-left flex gap-3 items-start bg-bg-elev p-3 border-l-2 transition-colors ${borderClass} ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      }`}
    >
      <div
        className={`w-5 h-5 shrink-0 mt-0.5 flex items-center justify-center text-[14px] font-bold border-2 ${boxClass}`}
      >
        {checked ? '✓' : ''}
      </div>
      <div className="flex-1 text-[12px] leading-[1.45] text-white">{label}</div>
    </button>
  );
}
